import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SpinnerGap } from "@phosphor-icons/react";

export default function AcceptInvite() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { applyAuth } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/auth/invite/${token}`)
      .then(({ data }) => setInvite(data))
      .catch((e) => setError(apiError(e.response?.data?.detail)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/accept-invite", { token, ...form });
      applyAuth(data);
      toast.success("Welcome to the team");
      nav("/app");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 grain">
      <div className="w-full max-w-sm bg-white border border-zinc-200 p-8 vf-fade-up relative z-10">
        <Logo className="mb-6" />
        {error ? (
          <p data-testid="invite-error" className="text-sm text-red-600">{error}</p>
        ) : !invite ? (
          <p className="text-sm text-zinc-400 font-mono">loading invite…</p>
        ) : (
          <>
            <h1 className="font-display font-bold text-2xl tracking-tight mb-1">Join {invite.org_name}</h1>
            <p className="text-sm text-zinc-500 mb-6">
              Invited as <span className="font-medium text-black">{invite.role}</span> · {invite.email}
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Your name</Label>
                <Input data-testid="invite-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Set password</Label>
                <Input data-testid="invite-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" />
              </div>
              <button data-testid="invite-submit" disabled={loading} className="w-full h-11 bg-black text-white rounded-md font-semibold hover:bg-zinc-800 transition-colors active:scale-95 flex items-center justify-center gap-2">
                {loading && <SpinnerGap size={18} className="animate-spin" />} Accept invitation
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
